/**
 * P-007 c2/c17 — intelligent sync engine.
 *
 * On reconnect (or when connectivity improves) drains the offline edit queue in health-criticality
 * order, bandwidth-aware: under a DEGRADED link only the safety-critical tier is sent; when ONLINE
 * the whole queue drains. Each edit is uploaded via an injected uploadFn; on a server conflict the
 * per-record-type conflictResolution strategy is applied and (if auto-resolvable) the resolved value
 * is re-submitted. Successfully-synced edits are removed from the queue. Registers with a
 * ConnectivityMonitor so it fires automatically when the mode transitions out of OFFLINE.
 */
import { selectForBandwidth } from './priority';
import { resolveConflict } from './conflictResolution';
import { getQueue, dequeue } from './offlineStore';
import { MODE } from './connectivity';

export class SyncEngine {
  constructor({ uploadFn, store = { getQueue, dequeue } } = {}) {
    this._upload = uploadFn;            // async (edit) -> {ok} | {conflict, remote}
    this._store = store;
    this._running = false;
    this._lastResult = null;
  }

  /** Drain the queue for the given connectivity mode. Returns a summary {sent, deferred, conflicts, needsUser}. */
  async drain(mode = MODE.ONLINE) {
    if (this._running || mode === MODE.OFFLINE) return { sent: 0, deferred: 0, conflicts: 0, needsUser: 0, skipped: true };
    this._running = true;
    const summary = { sent: 0, deferred: 0, conflicts: 0, needsUser: 0 };
    try {
      const queue = await this._store.getQueue();
      const selected = selectForBandwidth(queue, mode);
      const selectedSeqs = new Set(selected.map((e) => e.seq));
      summary.deferred = queue.length - selected.length;

      for (const edit of selected) {
        const res = await this._upload(edit);
        if (res && res.ok) {
          await this._store.dequeue(edit.seq);
          summary.sent++;
        } else if (res && res.conflict) {
          summary.conflicts++;
          const resolution = resolveConflict(edit.type, { value: edit.value, updatedAt: edit.updatedAt, deviceId: edit.deviceId }, res.remote);
          if (resolution.needsUser) {
            summary.needsUser++; // leave in queue, flagged for the patient
          } else {
            const retry = await this._upload({ ...edit, value: resolution.resolved, resolvedBy: resolution.strategy });
            if (retry && retry.ok) { await this._store.dequeue(edit.seq); summary.sent++; }
          }
        }
        // transient failure → leave in queue for the next drain
      }
      // anything not selected under a DEGRADED link stays queued (already counted in deferred)
      void selectedSeqs;
      this._lastResult = summary;
      return summary;
    } finally {
      this._running = false;
    }
  }

  /** Auto-drain whenever connectivity leaves OFFLINE. Returns an unsubscribe fn. */
  bindTo(monitor) {
    return monitor.subscribe((mode) => { if (mode !== MODE.OFFLINE) this.drain(mode); });
  }
}
